/** 2 -> 3: the open-field scatter gets its own RNG stream.
 *
 * `battle/field.ts` rolls the rocks and trees an open-field battle is fought
 * over. It used to roll them off `battle.ability`, because `RNG_STREAMS` was
 * closed to the file that wanted them; it now rolls them off `battle.field`.
 * A save written before that stream existed has no state for it, and D5 says
 * every named stream lives in state, so the rung's job is to put one there.
 *
 * `seedStream(rootSeed, "battle.field")` is the value `seedAllStreams` would
 * have written at game creation, and `rootSeed` is on every save - so an old
 * game resumes on exactly the stream it would have had if the name had existed
 * from the start, rather than on an arbitrary constant.
 *
 * A save paused mid-battle carries a second copy of the `battle.*` streams on
 * `BattleState.rng`, and it gets the same value. Nothing reads `battle.field`
 * after the board is laid, so the value cannot change how that battle plays;
 * it is written because a stream missing from state is the shape violation,
 * not because the fight needs it.
 */

import { seedStream } from "../rng/pcg32.js";
import type { Migration } from "./types.js";
import { isRecord, readSave, recordAt, writeSave, type Tree } from "./tree.js";

const STREAM = "battle.field";

/** Add the stream to one `rng` map, keeping any value already there. */
function withField(rng: Tree, rootSeed: number): Tree {
  if (Object.prototype.hasOwnProperty.call(rng, STREAM)) return rng;
  return { ...rng, [STREAM]: { ...seedStream(rootSeed, STREAM) } };
}

export const migration0003: Migration = {
  from: 2,
  to: 3,
  describe: "give the open-field obstacle scatter its own RNG stream",
  migrate(raw: unknown): Record<string, unknown> | null {
    const save = readSave(raw);
    if (save === null) return null;
    const rng = recordAt(save.state, "rng");
    if (rng === null) return null;
    const rootSeed = save.state["rootSeed"];
    if (typeof rootSeed !== "number" || !Number.isInteger(rootSeed)) return null;

    let state: Tree = { ...save.state, rng: withField(rng, rootSeed) };
    const battle = save.state["battle"];
    if (isRecord(battle)) {
      const bRng = recordAt(battle, "rng");
      if (bRng === null) return null;
      state = { ...state, battle: { ...battle, rng: withField(bRng, rootSeed) } };
    }
    return writeSave(save, state, 3);
  },
};
